import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSend, FiSave, FiPrinter } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff' };

const Input = ({ label, readOnly, ...props }) => (
    <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>{label}</label>
        <input {...props} readOnly={readOnly} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, color: readOnly ? C.sub : C.text, background: readOnly ? '#f9fafb' : C.white, outline: 'none', boxSizing: 'border-box', ...(props.style || {}) }} />
    </div>
);

const Select = ({ label, children, ...props }) => (
    <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>{label}</label>
        <select {...props} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, color: C.text, background: C.white, outline: 'none', boxSizing: 'border-box' }}>
            {children}
        </select>
    </div>
);

const emptyQ = () => ({ question_text: '', question_type: 'mcq', marks: 1, option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'A', ideal_answer: '', requires_upload: false, upload_title: '', image_url: '', uploading_image: false });

const CreateExamPaper = ({ editMode = false }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [assignments, setAssignments] = useState([]);
    const [form, setForm] = useState({ title: '', subject_code: '', subject_name: '', department: '', batch_name: '', semester: '', academic_year: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1), exam_type: 'semester', duration_mins: 180, total_marks: 100, instructions: '' });
    const [questions, setQuestions] = useState([emptyQ()]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                // Load assignments
                const assignRes = await api.get('/api/teacher/assignments');
                setAssignments(assignRes.data.assignments || []);

                // If editing, load the draft paper
                if (editMode && id) {
                    const paperRes = await api.get(`/api/teacher/papers/${id}`);
                    const p = paperRes.data.paper;
                    if (p) {
                        setForm({
                            title: p.title || '',
                            subject_code: p.subject_code || '',
                            subject_name: p.subject_name || '',
                            department: p.department || '',
                            batch_name: p.batch_name || '',
                            semester: p.semester || '',
                            academic_year: p.academic_year || '',
                            exam_type: p.exam_type || 'semester',
                            duration_mins: p.duration_mins || 180,
                            total_marks: p.total_marks || 100,
                            instructions: p.instructions || ''
                        });
                        if (paperRes.data.questions && paperRes.data.questions.length > 0) {
                            setQuestions(paperRes.data.questions);
                        }
                    }
                }
            } catch (err) {
                console.error(err);
                setError('Failed to load data.');
            } finally {
                setLoadingData(false);
            }
        };
        load();
    }, [editMode, id]);

    const setForm_ = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setQ = (i, k, v) => setQuestions(qs => qs.map((q, idx) => idx === i ? { ...q, [k]: v } : q));
    const addQ = () => setQuestions(qs => [...qs, emptyQ()]);
    const removeQ = (i) => setQuestions(qs => qs.filter((_, idx) => idx !== i));

    const handleQuestionImageUpload = async (i, file) => {
        if (!file) return;
        setQ(i, 'uploading_image', true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await api.post('/api/teacher/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setQ(i, 'image_url', res.data.url);
        } catch (err) {
            console.error(err);
            alert('Failed to upload question image.');
        } finally {
            setQ(i, 'uploading_image', false);
        }
    };

    // Auto-fill from assignment selection
    const fillFromAssignment = (val) => {
        if (!val) {
            setForm(f => ({ ...f, subject_code: '', subject_name: '', department: '', batch_name: '', semester: '', academic_year: '' }));
            return;
        }
        const a = assignments.find(a => `${a.subject_code}__${a.batch_name}__${a.semester}` === val);
        if (a) setForm(f => ({
            ...f,
            subject_code: a.subject_code ?? '',
            subject_name: a.subject_name ?? '',
            department: a.department ?? '',
            batch_name: a.batch_name ?? '',
            semester: a.semester ?? '',
            academic_year: a.academic_year ?? ''
        }));
    };

    const handleSave = async (submitAfter = false) => {
        setError('');
        if (!form.title || !form.subject_code || !form.batch_name || !form.semester) {
            setError('Please fill in all required fields.'); return;
        }
        setSaving(true);
        try {
            let paperId = id;
            if (editMode && id) {
                await api.put(`/api/teacher/papers/${id}`, { ...form, questions });
            } else {
                const res = await api.post('/api/teacher/papers', { ...form, questions });
                paperId = res.data.paperId;
            }
            
            if (submitAfter && paperId) {
                await api.put(`/api/teacher/papers/${paperId}/submit`);
            }
            navigate('/teacher/papers');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to save paper.');
        } finally { setSaving(false); }
    };

    return (
        <div style={{ width: '100%', fontFamily: font }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
                <h1 style={{ fontSize: 20, fontWeight: 900, color: C.text, margin: 0, letterSpacing: -0.4 }}>✏️ {editMode ? 'Edit Exam Paper' : 'Create Exam Paper'}</h1>
                <button onClick={() => window.print()} className="no-print" disabled={!form.subject_code} style={{ padding: '8px 16px', borderRadius: 20, background: '#fff', border: `1px solid ${C.border}`, color: !form.subject_code ? '#9ca3af' : C.text, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, cursor: !form.subject_code ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <FiPrinter size={16} /> Download PDF Preview
                </button>
            </div>

            {error && <div style={{ padding: '10px 16px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

            <div className="no-print" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                {/* Paper Details */}
                <div style={{ background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 3px 12px rgba(99,102,241,0.07)' }}>
                    <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 13, color: C.text }}>📄 Paper Details</div>
                    <div style={{ padding: '20px 20px 8px' }}>
                        {loadingData ? (
                            <div style={{ padding: 20, textAlign: 'center', color: C.sub, fontSize: 13, fontFamily: font }}>Loading assignments...</div>
                        ) : assignments.length === 0 ? (
                            <div style={{ padding: 20, textAlign: 'center', background: '#fef2f2', borderRadius: 12, border: '1px solid #fca5a5', color: '#dc2626', fontSize: 13, fontFamily: font, marginBottom: 16 }}>
                                You have not been assigned any batches by the admin. You cannot create exam papers until you are assigned a batch.
                            </div>
                        ) : (
                            <>
                                <Select label="Select Assigned Batch *" value={form.subject_code ? `${form.subject_code}__${form.batch_name}__${form.semester}` : ''} onChange={e => fillFromAssignment(e.target.value)}>
                                    <option value="">— Select an assignment —</option>
                                    {assignments.map(a => <option key={`${a.subject_code}__${a.batch_name}__${a.semester}`} value={`${a.subject_code}__${a.batch_name}__${a.semester}`}>{a.subject_name} ({a.subject_code}) · {a.batch_name} · Sem {a.semester}</option>)}
                                </Select>

                                <Input label="Paper Title *" placeholder="e.g. Mid Semester Exam 2024" value={form.title} onChange={e => setForm_('title', e.target.value)} disabled={!form.subject_code} />
                                <Input label="Subject Code" value={form.subject_code ?? ''} readOnly />
                                <Input label="Subject Name" value={form.subject_name ?? ''} readOnly />
                                <Input label="Department" value={form.department ?? ''} readOnly />
                                <Input label="Batch" value={form.batch_name ?? ''} readOnly />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <Input label="Semester" value={form.semester ?? ''} readOnly />
                                    <Input label="Academic Year" value={form.academic_year ?? ''} readOnly />
                                </div>
                                <Select label="Exam Type" value={form.exam_type} onChange={e => setForm_('exam_type', e.target.value)} disabled={!form.subject_code}>
                                    <option value="internal">Internal</option>
                                    <option value="semester">Semester</option>
                                    <option value="supply">Supply</option>
                                </Select>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <Input label="Duration (mins)" type="number" value={form.duration_mins} onChange={e => setForm_('duration_mins', e.target.value)} disabled={!form.subject_code} />
                                    <Input label="Total Marks" type="number" value={form.total_marks} onChange={e => setForm_('total_marks', e.target.value)} disabled={!form.subject_code} />
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BL, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Instructions</label>
                                    <textarea value={form.instructions} onChange={e => setForm_('instructions', e.target.value)} disabled={!form.subject_code} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: !form.subject_code ? '#f9fafb' : C.white }} placeholder="General exam instructions..." />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Questions */}
                <div style={{ background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 3px 12px rgba(99,102,241,0.07)' }}>
                    <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 800, fontSize: 13, color: C.text }}>❓ Questions ({questions.length})</span>
                        <button onClick={addQ} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, border: 'none', background: BL, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                            <FiPlus size={13} /> Add Question
                        </button>
                    </div>
                    <div style={{ padding: '12px 16px', maxHeight: 600, overflowY: 'auto' }}>
                        {questions.map((q, i) => (
                            <div key={i} style={{ background: C.bg, borderRadius: 14, padding: '14px 16px', marginBottom: 14, border: `1px solid ${C.border}`, position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: BL }}>Q{i + 1}</span>
                                    {questions.length > 1 && <button onClick={() => removeQ(i)} style={{ border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 8, padding: '4px 8px', cursor: 'pointer' }}><FiTrash2 size={12} /></button>}
                                </div>
                                <textarea value={q.question_text} onChange={e => setQ(i, 'question_text', e.target.value)} rows={2} placeholder="Question text..." style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font, resize: 'vertical', boxSizing: 'border-box', outline: 'none', marginBottom: 8 }} />
                                
                                {/* Question Image Logic */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '10px 12px', background: '#fff', borderRadius: 8, border: `1px dashed ${C.border}` }}>
                                    <label style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                        🖼️ Attach Image:
                                        <input type="file" accept="image/*" onChange={(e) => handleQuestionImageUpload(i, e.target.files[0])} disabled={q.uploading_image} style={{ fontSize: 11, marginLeft: 6, maxWidth: 180 }} />
                                    </label>
                                    {q.uploading_image && <span style={{ fontSize: 12, color: BL, fontWeight: 700 }}>Uploading...</span>}
                                    {q.image_url && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <a href={api.defaults.baseURL + q.image_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#059669', fontWeight: 600, textDecoration: 'none' }}>View Image</a>
                                            <button onClick={() => setQ(i, 'image_url', '')} style={{ border: 'none', background: '#fee2e2', color: '#ef4444', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                    <select value={q.question_type} onChange={e => setQ(i, 'question_type', e.target.value)} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none' }}>
                                        <option value="mcq">MCQ</option>
                                        <option value="short">Short Answer</option>
                                        <option value="long">Long Answer</option>
                                    </select>
                                    <input type="number" value={q.marks} onChange={e => setQ(i, 'marks', e.target.value)} placeholder="Marks" style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none' }} />
                                </div>
                                {q.question_type === 'mcq' && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        {['A', 'B', 'C', 'D'].map(opt => (
                                            <input key={opt} value={q[`option_${opt.toLowerCase()}`]} onChange={e => setQ(i, `option_${opt.toLowerCase()}`, e.target.value)} placeholder={`Option ${opt}`} style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none' }} />
                                        ))}
                                        <select value={q.correct_option} onChange={e => setQ(i, 'correct_option', e.target.value)} style={{ gridColumn: '1/-1', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: font, outline: 'none', marginTop: 4, background: '#ecfdf5', color: '#059669' }}>
                                            <option value="A">Correct Answer: A</option>
                                            <option value="B">Correct Answer: B</option>
                                            <option value="C">Correct Answer: C</option>
                                            <option value="D">Correct Answer: D</option>
                                        </select>
                                    </div>
                                )}
                                {(q.question_type === 'short' || q.question_type === 'long') && (
                                    <div style={{ marginTop: 8 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 4 }}>
                                            ✨ Ideal Reference Answer (used for AI Auto-Grading logic match)
                                        </label>
                                        <textarea value={q.ideal_answer || ''} onChange={e => setQ(i, 'ideal_answer', e.target.value)} rows={2} placeholder="Type the core logic or correct answer here. The AI will evaluate the student's answer against this reference." style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid #10b981`, fontSize: 13, fontFamily: font, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: '#ecfdf5' }} />
                                    </div>
                                )}
                                <div style={{ marginTop: 8, padding: '10px 12px', background: '#fff', borderRadius: 8, border: `1px solid ${C.border}` }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: C.text, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={q.requires_upload} onChange={e => setQ(i, 'requires_upload', e.target.checked)} style={{ cursor: 'pointer' }} />
                                        Enable File Upload for this Question
                                    </label>
                                    {q.requires_upload && (
                                        <div style={{ marginTop: 10 }}>
                                            <input type="text" value={q.upload_title} onChange={e => setQ(i, 'upload_title', e.target.value)} placeholder="Upload Title (e.g. 'Upload Rough Work', 'Upload Graph')" style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box' }} />
                                            <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>Students will be prompted to upload a file (PDF/Image) with this title.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="no-print" style={{ display: 'flex', gap: 12, marginTop: 24, marginBottom: 40, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button onClick={() => handleSave(false)} disabled={saving || assignments.length === 0 || !form.subject_code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 24px', borderRadius: 12, border: `1.5px solid ${assignments.length === 0 || !form.subject_code ? '#f3f4f6' : C.border}`, background: assignments.length === 0 || !form.subject_code ? '#f9fafb' : C.white, color: assignments.length === 0 || !form.subject_code ? '#9ca3af' : C.text, fontWeight: 700, fontSize: 14, cursor: assignments.length === 0 || !form.subject_code ? 'not-allowed' : 'pointer', fontFamily: font }}>
                    <FiSave size={15} /> Save as Draft
                </button>
                <button onClick={() => handleSave(true)} disabled={saving || assignments.length === 0 || !form.subject_code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 24px', borderRadius: 12, border: 'none', background: assignments.length === 0 || !form.subject_code ? '#d1d5db' : 'linear-gradient(135deg,#818cf8,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: assignments.length === 0 || !form.subject_code ? 'not-allowed' : 'pointer', fontFamily: font, boxShadow: '0 4px 14px rgba(99,102,241,0.3)' }}>
                    <FiSend size={15} /> {saving ? 'Saving…' : 'Save & Submit'}
                </button>
            </div>

            {/* Live PDF Preview Pane */}
            <div className="no-print" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: 0 }}>Live PDF Preview</h2>
                <span style={{ fontSize: 13, color: C.sub }}>This is exactly how it will look when downloaded/printed</span>
            </div>

            <div className="preview-pane" style={{ background: '#fff', color: '#000', fontFamily: 'serif', padding: '40px 50px', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #cbd5e1', maxWidth: 850, margin: '0 auto', boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 30 }}>
                    <img src="/svu-logo.png" alt="SVU Logo" style={{ height: 80, marginBottom: 10 }} />
                    <h2 style={{ margin: '0 0 5px', fontSize: 22, textTransform: 'uppercase' }}>Swami Vivekananda University</h2>
                    <h3 style={{ margin: '0 0 5px', fontSize: 18 }}>{form.title || 'Exam Title'}</h3>
                    <p style={{ margin: 0, fontSize: 14 }}>
                        {form.subject_name} ({form.subject_code}) &nbsp;|&nbsp;
                        Batch: {form.batch_name} &nbsp;|&nbsp;
                        Semester: {form.semester} &nbsp;|&nbsp;
                        Duration: {form.duration_mins} mins &nbsp;|&nbsp;
                        Full Marks: {form.total_marks}
                    </p>
                </div>

                {form.instructions && (
                    <div style={{ marginBottom: 20, paddingBottom: 15, borderBottom: '1px solid #ccc', fontSize: 14 }}>
                        <strong>Instructions:</strong> {form.instructions}
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {questions.map((q, idx) => (
                        <div key={idx} style={{ breakInside: 'avoid' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 15 }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <strong>{idx + 1}.</strong>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{q.question_text || '[Empty Question]'}</span>
                                        {q.image_url && (
                                            <div style={{ marginTop: 8 }}>
                                                <img src={api.defaults.baseURL + q.image_url} alt="Question Graphic" style={{ maxWidth: 400, maxHeight: 300, objectFit: 'contain', border: '1px solid #cbd5e1', borderRadius: 8 }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <strong>[{q.marks}]</strong>
                            </div>

                            {q.question_type === 'mcq' && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, paddingLeft: 24, fontSize: 14 }}>
                                    {['A', 'B', 'C', 'D'].map(opt => {
                                        const val = q[`option_${opt.toLowerCase()}`];
                                        if (!val) return <div key={opt}>({opt.toLowerCase()}) </div>;
                                        return (
                                            <div key={opt}>
                                                ({opt.toLowerCase()}) {val}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {q.requires_upload && (
                                <div style={{ fontStyle: 'italic', fontSize: 13, color: '#555', marginTop: 8, paddingLeft: 24 }}>
                                    * {q.upload_title || 'File upload required for this question.'}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @media print {
                    body { background: white !important; margin: 0; padding: 0; }
                    .no-print { display: none !important; }
                    .preview-pane { box-shadow: none !important; border: none !important; padding: 0 !important; max-width: none !important; margin: 0 !important; }
                    @page { margin: 20mm; }
                }
            `}</style>
        </div>
    );
};

export default CreateExamPaper;
